import Image, { ImageProps } from 'next/image';
import Link from 'next/link';
import { twMerge } from 'tailwind-merge';
import { truncateText } from '../../utils/truncateText';
import YoutubePlayButton from './svg/YoutubePlayButton';
import { ICommonCategoryMeta } from '../../models/ICategory';
import FallBackImage from './FallBackImage';
import { IAuthor } from '../../models/IAuthor';
import dynamic from 'next/dynamic';
import { DOMAttributes } from 'react';
import PostBadge from './svg/PostBadge';
const DateRenderer = dynamic(() => import('./DateRenderer'), {loading: () => null});

interface Props {
  title: string;
  image: ImageProps;
  uniqueCode: string;
  category: ICommonCategoryMeta;
  writer?: IAuthor;
  customCategory?: JSX.Element;
  customTitle?: JSX.Element;
  description?: string;
  publishedOn?: string | Date;
  maxLen?: number;
  type: number;
  automated?: boolean;
  targetType?: number;
  orientation?: 'horizontal' | 'vertical';
  mode?: 'normal' | 'image-bg';
  targetSlug?: string;
  isVideo?: boolean;
  isLarge?: boolean;
  hide?: Partial<{
    category: boolean;
    publishedOn: boolean;
    description: boolean;
  }>;
  classNames?: Partial<{
    wrapper: string;
    image: string;
    category: string;
    title: string;
    contentContainer: string;
    writer: string;
    description: string;
  }>;
  thumbnail?: string;
  exclusive?: boolean;
  exclusiveMobileOnly?: boolean;
  newsType: number;
  badge?: {
    [key in 'mobile' | 'tablet' | 'desktop']?: 'image' | 'inline'
  }
  props?: Partial<DOMAttributes<HTMLElement>>
}

export default function Post({
  title, 
  image, 
  maxLen, 
  orientation = 'vertical',
  type,
  category, 
  uniqueCode, 
  classNames, 
  publishedOn, 
  description,
  isLarge,
  exclusive,
  exclusiveMobileOnly,
  automated = false,
  targetType,
  targetSlug,
  hide,
  mode = 'normal',
  customCategory,
  customTitle,
  writer,
  thumbnail,
  newsType,
  badge,
  props = {}
}: Props) {
  const isHorizontal = orientation === 'horizontal';
  const isImageBg = mode === 'image-bg';
  const categoryUrl = `/${category.uniqueCode}`;
  const postUrl = `${categoryUrl}/${uniqueCode}`;
  const isVideo = type === 1;

  const isExclusive = newsType === 1;
  const isPrivate = newsType === 2;


  const Category = () => (
    <>
      {
        customCategory ? customCategory :
        category && !hide?.category ? (
          <Link 
            title={category.title} 
            href={categoryUrl} 
            className={twMerge('flex items-center gap-x-[8px] mb-[8px] w-fit h6 text-primary', classNames?.category)}
          >
            {category.title}
          </Link>
        ) : null}
      </>
  )

  const Writer = () =>  {
  if (!writer) return null;   
    return (
    <Link 
      title={writer.name} 
      href={`/writers/${writer?.uniqueCode}`} 
      className={twMerge('flex items-center justify-center gap-x-[8px] mt-[16px]', classNames?.writer)}
    >
      <h5>{writer.name}</h5>
      <div className='h-[2px] w-[18px] bg-secondary'/>
    </Link>
  )
}

  if (isImageBg) {
    return (
      <div className={twMerge('flex flex-col', classNames?.wrapper)} {...props}>
      <div className={twMerge('flex flex-col justify-between p-[16px] gap-y-[16px] relative', isLarge ? 'aspect-[16/9]' : 'aspect-[4/3]' )}>
        <Link href={postUrl} title={title} className={twMerge('absolute left-0 top-0 h-full w-full',classNames?.image)}>
          <FallBackImage {...image} thumbnail={thumbnail} isLarge={isLarge}  className={twMerge('w-full h-full object-cover',image.className)} />
          {isVideo ? <YoutubePlayButton className='absolute right-[5px] bottom-[5px] w-[30px] h-[30px] rounded-full z-[2]'/> : null}
          <div className='w-full absolute bottom-0 left-0 h-[60%] bg-gradient-to-t from-gray-900 to-transparent'/>
        </Link>


          {hide?.category ? <div/> : <Link 
            title={category.title} 
            href={categoryUrl} 
            className={twMerge('grid place-items-center px-[12px] py-[5px] bg-secondary w-fit rounded-[4px] h5 text-gray-1000 dark:text-white', classNames?.category)}
          >
            {category.title}
          </Link>}
        {/* {hide?.description ? null : (
            <Link title={title} href={postUrl} className='w-fit'>
            {customTitle ? customTitle : <h3 className={twMerge('text-white h5', classNames?.title)}>{maxLen ? truncateText(title, maxLen) : title}</h3>}
           </Link>
          
       
          // <Link title={title} href={postUrl} className='w-fit'><h3 className={twMerge('line-clamp-3 h5 text-white', classNames?.title)}>{title}</h3></Link>
        )} */}
      </div>
      </div>
    )
  }

  const PostTitleJsx = () => {
    const isSpecial = (isExclusive || isPrivate || Object.values(badge || {}).some(x => x === 'inline'));
  
    if (!isSpecial) {
      return (
        <Link title={title} href={postUrl} className='w-fit'>
          {customTitle ? customTitle : <h3 className={twMerge('!font-medium',classNames?.title)}>{maxLen ? truncateText(title, maxLen) : title}</h3>}
        </Link>
      )
    }
  
    return (
      <div className="flow-root"> 
        {isExclusive ? (
          <div className={twMerge('float-right w-[62px] h-[31px] ml-[16px] hidden',badge?.mobile === 'inline' && 'inline-block', badge?.tablet === 'inline' && 'tablet:inline-block', badge?.desktop === 'inline' && 'desktop:inline-block')}>
            <PostBadge className='absolute top-0 left-0 [&>*]:fill-[#775b73]'/>
            <span className='absolute top-[5px] leading-0 right-[6px] text-white font-bold'>حصري</span>
            </div>
        ) : isPrivate ? (
            <div className={twMerge('float-right w-[62px] h-[31px] ml-[16px] hidden', badge?.mobile === 'inline' && 'inline-block', badge?.tablet === 'inline' && 'tablet:inline-block', badge?.desktop === 'inline' && 'desktop:inline-block')}>
            <PostBadge className='absolute top-0 left-0 [&>*]:fill-secondary'/>
            <span className='absolute top-[5px] leading-0 right-[10px] text-white font-bold'>خاص</span>
            </div>
        ) : null}
        <Link title={title} href={postUrl} className='w-fit'>
          {customTitle ? customTitle : <h3 className={twMerge('!font-medium',classNames?.title)}>{maxLen ? truncateText(title, maxLen) : title}</h3>}
        </Link>
      </div>
    );
  };
  
  return (
    <div className={twMerge('relative flex h-fit w-full shadow-post dark:shadow-darkPost', isHorizontal ? 'flex-row items-center gap-x-[8px]' : 'flex-col', classNames?.wrapper)} {...props}>
      <Link href={postUrl} title={title} className={twMerge('relative w-full mb-[16px] flex-shrink-0', isLarge ? 'aspect-[16/9]' : 'aspect-[4/3]', classNames?.image)}>
        <FallBackImage isLarge={isLarge} {...image}  thumbnail={thumbnail} className={twMerge('w-full h-full object-cover',image.className)} />
        {isVideo ? <YoutubePlayButton className='absolute right-[5px] bottom-[5px] w-[30px] h-[30px] rounded-full'/> : null}
      {isExclusive &&  Object.values(badge || {}).some(x => x === 'image') ? <div className={twMerge('absolute right-0 z-[2] rounded-tr-[12px] rounded-bl-[12px] h-[33px] w-[68px] top-0 px-[12px] py-[6px] text-white bg-secondary place-items-center !text-[16px] hidden', badge?.mobile === 'image' && 'grid tablet:hidden', badge?.tablet === 'image' && 'tablet:grid desktop:hidden', badge?.desktop === 'image' && 'desktop:grid')}>حصري</div> : null}
      {isPrivate &&  Object.values(badge || {}).some(x => x === 'image') ? <div className={twMerge('absolute right-0 z-[2] rounded-tr-[12px] rounded-bl-[12px] h-[33px] w-[68px] top-0 px-[12px] py-[6px] text-white bg-primary place-items-center !text-[16px] hidden', badge?.mobile === 'image' && 'grid tablet:hidden', badge?.tablet === 'image' && 'tablet:grid desktop:hidden', badge?.desktop === 'image' && 'desktop:grid')}>خاص</div> : null}
      </Link>
      <div className={twMerge(
        'flex flex-col h-full relative',
        classNames?.contentContainer,
        )}>
        {Object.values(badge || {}).some(x => x === 'inline') ? (
          <div className='flow-root'>

          {isExclusive ? <div className={twMerge('absolute right-[-16px] top-[-16px] z-[2] rounded-tr-[12px] rounded-bl-[12px] h-[33px] w-[68px] px-[12px] py-[6px] text-white bg-secondary place-items-center !text-[16px] hidden', badge?.mobile === 'inline' && 'grid tablet:hidden', badge?.tablet === 'inline' && 'tablet:grid desktop:hidden', badge?.desktop === 'inline' && 'desktop:grid')}>حصري</div> : null}
          {isPrivate ? <div className={twMerge('absolute right-[-16px] top-[-16px] z-[2] rounded-tr-[12px] rounded-bl-[12px] h-[33px] w-[68px] px-[12px] py-[6px] text-white bg-primary place-items-center !text-[16px] hidden', badge?.mobile === 'inline' && 'grid tablet:hidden', badge?.tablet === 'inline' && 'tablet:grid desktop:hidden', badge?.desktop === 'inline' && 'desktop:grid')}>خاص</div> : null}
            {isExclusive || isPrivate ? <div className={twMerge('float-right', badge?.mobile === 'inline' && 'w-[60px] h-[28px] tablet:h-0 tablet:w-0', badge?.tablet === 'inline' && 'tablet:h-[28px] tablet:w-[60px] desktop:h-0 desktop:w-0', badge?.desktop === 'inline' && 'desktop:w-[60px]',)}/>           : null}

          <Link title={title} href={postUrl} className={twMerge('!inline')}>
          {customTitle ? customTitle : <h3 className={twMerge('!font-medium !inline ',classNames?.title)}>{maxLen ? truncateText(title, maxLen) : title}</h3>}
         </Link>
          </div>

        ) : (
            <Link title={title} href={postUrl} className={twMerge('w-fit')}>
          {customTitle ? customTitle : <h3 className={twMerge('!font-medium',classNames?.title)}>{maxLen ? truncateText(title, maxLen) : title}</h3>}
         </Link>
        )}
        {/* <Category /> */}
 

        {/* <PostTitleJsx/> */}
        
     
        <Writer />
        {description ? <div className='mt-[16px] flex'><span className={twMerge('lg tablet:md', classNames?.description)}>{description}</span></div> : null}
        {publishedOn && !hide?.publishedOn ? <span className='text-gray-600 dark:text-gray-500 mt-[16px] md' suppressHydrationWarning={true}><DateRenderer date={publishedOn as string} format='hh:mm A, DD MMM YYYY' /></span> : null}
      </div>
    </div>
  )
}